import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'verne.theme';

function readClass(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function applyClass(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

// Source of truth on first paint is the inline script in index.html, which
// has already set the `dark` class on <html> based on (in order): explicit
// user override in localStorage, host default (kiosk/Tailscale), and the
// OS prefers-color-scheme. We mirror that into React state and write back
// to localStorage on user action so the choice survives reloads.
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => readClass());

  useEffect(() => {
    applyClass(theme);
  }, [theme]);

  // If the user has not made an explicit choice, follow OS pref changes
  // live. Once they toggle, their choice wins until they clear it.
  useEffect(() => {
    let stored: string | null = null;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch {}
    if (stored === 'light' || stored === 'dark') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setExplicit = useCallback((next: Theme) => {
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch {}
    setTheme(next);
  }, []);

  const toggle = useCallback(() => {
    setExplicit(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setExplicit]);

  return { theme, toggle, setTheme: setExplicit };
}
